import apiService, { setAccessToken, clearAccessToken, getToken, clearRefreshFailure, markRefreshFailed, handleRefreshTokenFromResponse, getStoredRefreshToken } from './api';
import { announceOrgSwitch, expectOrgSwitch, setTabOrgId } from '../lib/orgGuard';
import type {
  User,
  OrgMembership,
  AuthResponse,
  LoginCredentials,
  RegisterCredentials,
  EmailVerificationRequest,
  ResendVerificationRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  RefreshTokenResponse
} from '../types';

export interface PendingEmailChange {
  new_email: string;
  expires_at: string;
}

class AuthService {
  // Login user
  //
  // Returns a discriminated union:
  //   { status: 'ok', user, mustEnrollMfa? }  — fully logged in
  //   { status: 'mfa_required', mfaTicket, availableMethods, rememberMe }
  //     — caller should navigate to /mfa-challenge
  async login(credentials: LoginCredentials): Promise<
    | {
        status: 'ok';
        user: User;
        mustEnrollMfa?: boolean;
      }
    | {
        status: 'mfa_required';
        mfaTicket: string;
        availableMethods: { totp: boolean; email: boolean; passkey: boolean };
        rememberMe: boolean;
      }
  > {
    const { rememberMe = false, ...loginData } = credentials;
    const response = await apiService.post<any>('/auth/login', loginData);

    if (!response.success) {
      throw new Error(response.message || 'Login failed');
    }

    // MFA required — response.data holds { mfaTicket, availableMethods }
    if ((response as any).mfaRequired) {
      return {
        status: 'mfa_required',
        mfaTicket: response.data.mfaTicket,
        availableMethods: response.data.availableMethods,
        rememberMe,
      };
    }

    if (!response.data) throw new Error('Login failed');

    setAccessToken(response.data.accessToken, response.data.expiresAt);
    handleRefreshTokenFromResponse(response.data);
    clearRefreshFailure();

    return {
      status: 'ok',
      user: response.data.user,
      mustEnrollMfa: response.data.mustEnrollMfa,
    };
  }

  // Called by MfaChallenge after server verifies 2FA — stores the real session token.
  completeMfaLogin(user: User, accessToken: string, _rememberMe: boolean, responseData?: any): void {
    setAccessToken(accessToken, responseData?.expiresAt);
    if (responseData) {
      handleRefreshTokenFromResponse(responseData);
    }
    clearRefreshFailure();
    // user is passed back to AuthContext — no storage write needed
    void user;
  }

  // Register user
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    const { confirmPassword: _confirmPassword, rememberMe: _rememberMe = false, ...registerData } = credentials;
    const response = await apiService.post<{ user: User; accessToken: string; expiresAt?: string; refreshToken?: string }>('/auth/register', registerData);

    if (response.success && response.data) {
      setAccessToken(response.data.accessToken, response.data.expiresAt);
      handleRefreshTokenFromResponse(response.data);
      return response.data;
    }

    throw new Error('Registration failed');
  }

  // Logout user from current device
  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      markRefreshFailed();
      clearAccessToken();
    }
  }

  // Logout from all devices
  async logoutAll(): Promise<void> {
    try {
      await apiService.post('/auth/logout-all');
    } catch (error) {
      console.error('Logout all API call failed:', error);
    } finally {
      markRefreshFailed();
      clearAccessToken();
    }
  }

  // Permanently delete the current user's account (Apple Guideline 5.1.1(v)).
  // The server anonymises all personal data and disables the account; we then
  // clear the local session exactly like a logout.
  async deleteAccount(password: string): Promise<void> {
    const response = await apiService.post('/users/me/delete', { password });
    if (!response.success) {
      throw new Error(response.message || 'Account deletion failed');
    }
    markRefreshFailed();
    clearAccessToken();
  }

  // Changing your own email: the request mails a link to the new address, and
  // users.email only moves once that link is followed.
  async getPendingEmailChange(): Promise<PendingEmailChange | null> {
    const response = await apiService.get<{ pending: PendingEmailChange | null }>('/auth/change-email');
    return response.success ? response.data?.pending ?? null : null;
  }

  async requestEmailChange(newEmail: string, password: string): Promise<PendingEmailChange> {
    const response = await apiService.post<{ pending: PendingEmailChange }>('/auth/change-email', {
      new_email: newEmail,
      password,
    });
    if (!response.success || !response.data?.pending) {
      throw new Error(response.message || 'Could not start the email change');
    }
    return response.data.pending;
  }

  async cancelEmailChange(): Promise<void> {
    await apiService.delete('/auth/change-email');
  }

  async confirmEmailChange(token: string): Promise<string> {
    const response = await apiService.post<{ email: string }>('/auth/confirm-email-change', { token });
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Could not change your email address');
    }
    return response.data.email;
  }

  // Refresh access token (called automatically by API interceptor or on page load)
  async refreshToken(): Promise<RefreshTokenResponse> {
    // On native platforms the refresh token is stored in localStorage (no httpOnly cookie).
    // Pass it via header so the server can identify the session on a cold start (e.g.
    // Android pull-to-refresh wipes the in-memory access token and sessionStorage).
    const storedRt = getStoredRefreshToken();
    const headers = storedRt ? { 'X-Refresh-Token': storedRt } : undefined;
    const response = await apiService.post<RefreshTokenResponse>('/auth/refresh', {}, headers);

    if (response.success && response.data) {
      setAccessToken(response.data.accessToken, response.data.expiresAt);
      handleRefreshTokenFromResponse(response.data);
      return response.data;
    }

    throw new Error('Token refresh failed');
  }

  // Get current user from API
  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<User>('/auth/me');

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to get current user');
  }

  // Check if user is authenticated (in-memory check only)
  isAuthenticated(): boolean {
    return getToken() !== null;
  }

  // Email verification methods
  async verifyEmail(data: EmailVerificationRequest): Promise<void> {
    const response = await apiService.post<{ email: string; verified_at: string }>('/auth/verify-email', data);

    if (!response.success) {
      throw new Error(response.message || 'Email verification failed');
    }
  }

  async resendVerificationEmail(data: ResendVerificationRequest): Promise<void> {
    const response = await apiService.post<void>('/auth/resend-verification', data);

    if (!response.success) {
      throw new Error('Failed to resend verification email');
    }
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    const response = await apiService.post<void>('/auth/forgot-password', data);

    if (!response.success) {
      throw new Error('Failed to send password reset email');
    }
  }

  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    const response = await apiService.post<void>('/auth/reset-password', data);

    if (!response.success) {
      throw new Error('Password reset failed');
    }
  }

  // Fetch the orgs this user belongs to
  async getMyOrgs(): Promise<OrgMembership[]> {
    const response = await apiService.get<OrgMembership[]>('/users/me/orgs');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Failed to fetch organisations');
  }

  // Switch the active organisation.
  async switchOrg(orgId: number): Promise<User> {
    expectOrgSwitch(orgId);
    try {
      const response = await apiService.post<{
        active_organisation: { id: number; name: string };
      }>('/users/me/switch-org', { organisation_id: orgId });

      if (!response.success || !response.data) {
        throw new Error('Failed to switch organisation');
      }
    } catch (error) {
      expectOrgSwitch(null);
      throw error;
    }

    // From here every request this tab makes is for the new organisation, and
    // this browser's other tabs are told to follow.
    setTabOrgId(orgId);
    announceOrgSwitch(orgId);

    return this.getCurrentUser();
  }

  // What an invitation link is for: which organisation, who sent it, to whom.
  async getInvitation(token: string): Promise<InvitationInfo> {
    const response = await apiService.get<InvitationInfo>(`/auth/invite/${encodeURIComponent(token)}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('This invitation is not valid');
  }

  // Join the inviting organisation with the account already signed in.
  async acceptInvitation(token: string): Promise<{ organisation: { id: number; name: string }; role: string }> {
    const response = await apiService.post<{ organisation: { id: number; name: string }; role: string }>(
      `/auth/invite/${encodeURIComponent(token)}/accept`,
      {}
    );
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error('Could not accept the invitation');
  }
}

export interface InvitationInfo {
  organisationName: string;
  invitedEmail: string;
  invitedBy: string;
  expiresAt: string;
}

const authService = new AuthService();
export default authService;
